import { useContext, useEffect, useState } from "react";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { AddSavingsProductWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { createSavingsProductService } from "../../../../utils/api/configuration/savingsproduct/createSavingsProduct";
import Cookies from "universal-cookie";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseButton } from "../../../../components/button/styled";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { useNavigate } from "react-router-dom";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { useQuery } from "@tanstack/react-query";

export const AddSavingsProduct = () => {
    const accountTypes = ["Savings", "Current"];

    const navigate = useNavigate();
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        accountType: " ",
        name: "",
        linkedAccountGL: " ",
        savingsInterestGL: " ",
        interestRate: "",
        description: "",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    const { data: glData } = useQuery({
        queryKey: ["glData", TOKEN],
        queryFn: async () => {
            const response = await retrieveAllChartOfAccountService(TOKEN);
            return {
                linkedAccountGLs: response?.data?.filter((gl: Record<string, any>) => gl.glType === "200" && gl.glAccountType === "GL Account") ?? [],
                savingsInterestGLs: response?.data?.filter((gl: Record<string, any>) => gl.glType === "500" && gl.glAccountType === "GL Account") ?? [],
            };
        },
        enabled: !!TOKEN,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await createSavingsProductService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to create savings product. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to create savings product. ${error.message}`);
            console.error('Failed to create savings product:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Savings-Product"}
            pageTitle={"Add Savings Product"}
        >
            <AddSavingsProductWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Savings Product Added Successfully"
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Savings Products List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required information to add a savings product
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Account Type
                                </BaseLabel>
                                <BaseSelect
                                    name="accountType"
                                    fontweight={400}
                                    value={formDetails.accountType}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select Account Type
                                    </BaseOption>
                                    {accountTypes.map((accountType: string, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={accountType}
                                        >
                                            {accountType}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Name of Savings Product
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="name"
                                    value={formDetails.name}
                                    placeholder="Enter Savings Or Current Product Name"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Linked Account GL
                                </BaseLabel>
                                <BaseSelect
                                    name="linkedAccountGL"
                                    fontweight={400}
                                    value={formDetails.linkedAccountGL}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select GL Account
                                    </BaseOption>
                                    {glData?.linkedAccountGLs?.map((glAccount: Record<string, any>, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={glAccount.id}
                                        >
                                            {glAccount.glName?.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Interest (%)
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="interestRate"
                                    value={formDetails.interestRate}
                                    placeholder="Enter Percentage Interest Rate"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Savings Interest GL
                                </BaseLabel>
                                <BaseSelect
                                    name="savingsInterestGL"
                                    fontweight={400}
                                    value={formDetails.savingsInterestGL}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select Savings Interest GL
                                    </BaseOption>
                                    {glData?.savingsInterestGLs?.map((savingsInterestGL: Record<string, any>, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={savingsInterestGL.id}
                                        >
                                            {savingsInterestGL.glName?.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Description
                                </BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="description"
                                    value={formDetails.description}
                                    placeholder="Enter Description"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </AddSavingsProductWrapper>
        </AppLayout>
    )
}